import {
  Archivo,
  Atkinson_Hyperlegible,
  Bricolage_Grotesque,
  DM_Sans,
  Fraunces,
  JetBrains_Mono,
  Lora,
  Manrope,
  Playfair_Display,
  Sora,
  Source_Serif_4,
  Space_Grotesk,
  Syne,
  Unbounded,
} from 'next/font/google';
import type { CSSProperties } from 'react';
import type { PageFontId } from '@/lib/page-fonts';
import type { PageFonts } from '@/lib/product-design';

/**
 * The seller fonts' loaders: one per id in PAGE_FONTS (src/lib/page-fonts.ts),
 * self-hosted by next/font, so a page fetches them from our own origin
 * (`/_next/static/media`, which the CSP's `font-src 'self'` already allows)
 * and nothing reaches Google when it is viewed.
 *
 * EVERY ONE IS `preload: false`, and that is load-bearing. next/font preloads
 * a font on every route under the module that declares it, so with preloading
 * on, every product page and storefront would download all fourteen files
 * whatever its seller chose. Off, the @font-face rules are only CSS (a few KB,
 * on the routes that import this module), and a browser fetches a font file
 * the first time something on the page is actually set in it.
 *
 * `display: 'swap'` with next/font's automatic size-matched fallback: text
 * paints at once in a local face adjusted to the font's metrics and swaps when
 * the file lands, so the swap moves almost nothing. That needs the family in
 * next/font's metrics table (next/dist/server/capsize-font-metrics.json);
 * check a new font is there before adding it, or it falls back unadjusted.
 *
 * Every subset a family has (Latin, Latin Extended, Cyrillic, Vietnamese...)
 * is declared with its own unicode-range, so accented or non-Latin text stays
 * in the font, and a browser fetches only the files for characters the page
 * actually has. (`subsets` below only picks what next/font would PRELOAD,
 * which is nothing here; it is kept so turning preload on stays correct.)
 * Italics are loaded where the family has them, because descriptions and bios
 * carry <em>, and a synthesised slant of a serif is the one thing here that
 * would look broken.
 *
 * Imported only by the pages that render seller content (the storefront, the
 * product page) and the editors that preview it, so the rest of the site never
 * carries even the CSS. page-fonts.test.ts holds this file to the catalogue:
 * one loader per id, each with `preload: false`.
 */

const dmSans = DM_Sans({ subsets: ['latin', 'latin-ext'], style: ['normal', 'italic'], display: 'swap', preload: false });
const manrope = Manrope({ subsets: ['latin', 'latin-ext'], display: 'swap', preload: false });
const archivo = Archivo({ subsets: ['latin', 'latin-ext'], style: ['normal', 'italic'], display: 'swap', preload: false });
// The original family, not "Atkinson Hyperlegible Next": the newer one is
// missing from next/font's fallback-metrics table, so it would get no
// size-matched fallback (and the build logs an error for it). This one ships
// two weights, which next/font needs named for a static family.
const atkinsonHyperlegible = Atkinson_Hyperlegible({
  weight: ['400', '700'],
  subsets: ['latin', 'latin-ext'],
  style: ['normal', 'italic'],
  display: 'swap',
  preload: false,
});
const lora = Lora({ subsets: ['latin', 'latin-ext'], style: ['normal', 'italic'], display: 'swap', preload: false });
const sourceSerif = Source_Serif_4({ subsets: ['latin', 'latin-ext'], style: ['normal', 'italic'], display: 'swap', preload: false });
const spaceGrotesk = Space_Grotesk({ subsets: ['latin', 'latin-ext'], display: 'swap', preload: false });
const sora = Sora({ subsets: ['latin', 'latin-ext'], display: 'swap', preload: false });
const bricolageGrotesque = Bricolage_Grotesque({ subsets: ['latin', 'latin-ext'], display: 'swap', preload: false });
const syne = Syne({ subsets: ['latin', 'latin-ext'], display: 'swap', preload: false });
const unbounded = Unbounded({ subsets: ['latin', 'latin-ext'], display: 'swap', preload: false });
const playfairDisplay = Playfair_Display({ subsets: ['latin', 'latin-ext'], style: ['normal', 'italic'], display: 'swap', preload: false });
const fraunces = Fraunces({ subsets: ['latin', 'latin-ext'], style: ['normal', 'italic'], display: 'swap', preload: false });
const jetbrainsMono = JetBrains_Mono({ subsets: ['latin', 'latin-ext'], style: ['normal', 'italic'], display: 'swap', preload: false });

/**
 * Each font's CSS `font-family` value: next/font's generated family and its
 * size-matched fallback. Typed as a Record over PageFontId, so a catalogue id
 * with no loader (or a loader with no id) is a type error.
 */
const PAGE_FONT_FAMILIES: Readonly<Record<PageFontId, string>> = {
  'dm-sans': dmSans.style.fontFamily,
  'manrope': manrope.style.fontFamily,
  'archivo': archivo.style.fontFamily,
  'atkinson-hyperlegible': atkinsonHyperlegible.style.fontFamily,
  'lora': lora.style.fontFamily,
  'source-serif': sourceSerif.style.fontFamily,
  'space-grotesk': spaceGrotesk.style.fontFamily,
  'sora': sora.style.fontFamily,
  'bricolage-grotesque': bricolageGrotesque.style.fontFamily,
  'syne': syne.style.fontFamily,
  'unbounded': unbounded.style.fontFamily,
  'playfair-display': playfairDisplay.style.fontFamily,
  'fraunces': fraunces.style.fontFamily,
  'jetbrains-mono': jetbrainsMono.style.fontFamily,
};

/** One font's CSS family, for a picker option drawn in its own face. */
export function pageFontFamily(id: PageFontId): string {
  return PAGE_FONT_FAMILIES[id];
}

/**
 * The inline style that puts a page's content in its seller's fonts: the two
 * custom properties the `font-seller-heading` and `font-seller-body` families
 * read (tailwind.config.ts), or undefined when the page uses the site font for
 * both.
 *
 * Variables rather than a font-family on the wrapper, so nothing changes font
 * just by sitting inside the page: only the elements that opt in with one of
 * those two classes do. Prices, the buy box, the VAT notice, reviews and the
 * nav never carry them, so a later edit that moves one inside the wrapper still
 * cannot restyle it. Set on the page's root element, which renders on the
 * server, so the first HTML is already in the right fonts.
 */
export function pageFontStyle(fonts: PageFonts): CSSProperties | undefined {
  const style: Record<string, string> = {};
  if (fonts.heading) style['--seller-heading-font'] = PAGE_FONT_FAMILIES[fonts.heading];
  if (fonts.body) style['--seller-body-font'] = PAGE_FONT_FAMILIES[fonts.body];
  return Object.keys(style).length > 0 ? (style as CSSProperties) : undefined;
}
